import {
  SaveEngine,
  AutoSaveController,
  LocalStorageAdapter,
  type StorageAdapter,
  type SaveDataV1,
  type GameSerializeInput,
  type SlimeState,
  type Inventory,
  type MutationSystem,
  type BossEncounter,
  SEED_TRAITS
} from '@slimebound/core';
import type { SlimeController } from '../character/slime-controller.js';
import type { SlimeMesh } from '../character/slime-mesh.js';
import type { SettlementManager } from '../settlement/settlement-manager.js';
import type { NotificationLog } from '../ui/notification-log.js';

export interface ApplyStateTargets {
  slime: SlimeState;
  slimeController: SlimeController;
  slimeMesh?: SlimeMesh;
  inventory: Inventory;
  mutations: MutationSystem;
  settlementManager?: SettlementManager;
  bossEncounter?: BossEncounter;
  notificationLog?: NotificationLog;
}

export class GameSaveService {
  private autoSaveController: AutoSaveController;
  private adapter: StorageAdapter;

  constructor(adapter?: StorageAdapter, options?: { intervalSec?: number; onSave?: (data: SaveDataV1) => void }) {
    this.adapter = adapter ?? new LocalStorageAdapter();
    this.autoSaveController = new AutoSaveController(this.adapter, options);
  }

  public getAutoSaveController(): AutoSaveController {
    return this.autoSaveController;
  }

  public saveGame(input: GameSerializeInput): SaveDataV1 {
    const data = SaveEngine.serialize(input);
    this.autoSaveController.saveNow(data);
    return data;
  }

  public loadGame(): SaveDataV1 | null {
    return this.autoSaveController.load();
  }

  public exportSaveToJsonString(input: GameSerializeInput): string {
    return SaveEngine.exportToJsonString(input);
  }

  public importSaveFromJson(jsonStr: string): SaveDataV1 {
    const data = SaveEngine.importFromJsonString(jsonStr);
    this.autoSaveController.saveNow(data);
    return data;
  }

  public applySaveToGame(saveData: SaveDataV1, targets: ApplyStateTargets): void {
    // 1. Slime state & Controller position
    targets.slime.massKg = saveData.player.massKg;
    targets.slime.gelEnergy = saveData.player.gelEnergy;
    targets.slimeController.position.set(
      saveData.player.position.x,
      saveData.player.position.y,
      saveData.player.position.z
    );
    if (targets.slimeMesh) {
      targets.slimeMesh.update(
        targets.slimeController.position,
        targets.slimeController.velocity,
        targets.slimeController.wobble.getScale(),
        0
      );
    }

    // 2. Inventory restoration
    targets.inventory.clear();
    for (const item of saveData.inventory.items) {
      targets.inventory.addItem({
        itemId: item.itemId,
        name: item.name,
        quantity: item.quantity,
        unitMassKg: item.unitMassKg
      });
    }

    // 3. Mutations restoration
    if (saveData.mutations.absorbedCounts) {
      targets.mutations.setMaterialCounts(saveData.mutations.absorbedCounts as any);
    }
    for (const traitId of saveData.mutations.unlockedTraitIds) {
      const def = SEED_TRAITS.find((t) => t.id === traitId);
      if (def) {
        targets.mutations.unlockTrait(def);
      }
    }

    // 4. Dungeon / Boss progress
    if (targets.bossEncounter && saveData.dungeon) {
      if (saveData.dungeon.bossDefeated) {
        if (targets.bossEncounter.currentHp > 0) {
          targets.bossEncounter.takeDamage(targets.bossEncounter.maxHp);
        }
      }
    }

    targets.notificationLog?.add('💾 Game state loaded successfully!', 'success', 4.0);
  }

  public downloadSaveFile(input: GameSerializeInput): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const jsonStr = this.exportSaveToJsonString(input);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `slimebound_save_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
